import React from "react";
import {
  TableCell,
  Box,
  Typography,
  TextField,
  Tooltip,
  IconButton,
  Button,
  FormControlLabel,
  Switch,
  InputAdornment,
} from "@mui/material";
import {
  Info as InfoIcon,
  CheckCircle,
  CheckCircleOutline,
  Comment as CommentIcon,
  RemoveCircleOutline,
  ArticleOutlined,
} from "@mui/icons-material";
import type { RFQItem } from "../../../types/rfq";
import RemarkPopover from './RemarkPopover';
import { editableTextSx, numberFieldSx, switchLabelSx } from "./itemRowStyles";

// The editable cells of an RFQ item row (RFQItemRow holds their state).

type ChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => void;

/** The parsed source text, when it differs from the item name. */
function OriginalTextHint({ item }: { item: RFQItem }) {
  return (
    <Tooltip
      title={
        <Box sx={{ maxWidth: 400, p: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Original Parsed Text
          </Typography>
          <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {item.original_text}
          </Typography>
        </Box>
      }
      arrow
      placement="right"
    >
      <ArticleOutlined sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help', flexShrink: 0, mt: 0.25 }} />
    </Tooltip>
  );
}

/** A text field that saves on blur or Enter. */
function InlineEditField({ value, onChange, onSave, placeholder, fullWidth, minWidth }: {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  placeholder?: string;
  fullWidth?: boolean;
  minWidth: number;
}) {
  return (
    <TextField
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onSave}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          onSave();
        }
      }}
      autoFocus
      placeholder={placeholder}
      fullWidth={fullWidth}
      sx={{ minWidth }}
    />
  );
}

interface ItemNameCellProps {
  item: RFQItem;
  /** DIY / manual RFQs let the name and model be edited in place. */
  editable: boolean;
  itemName: string;
  setItemName: (name: string) => void;
  isEditingName: boolean;
  startEditingName: () => void;
  saveName: () => void;
  modelNumber: string;
  setModelNumber: (model: string) => void;
  isEditingModel: boolean;
  startEditingModel: () => void;
  saveModel: () => void;
}

/** Name and model, editable in place on a DIY or manual RFQ. */
export function ItemNameCell(p: ItemNameCellProps) {
  const { item } = p;
  const originalTextHint = item.original_text && item.original_text !== item.item_name && <OriginalTextHint item={item} />;

  let name: React.ReactNode;
  if (!p.editable) {
    name = (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.item_name}</Typography>
        {originalTextHint}
      </Box>
    );
  } else if (p.isEditingName) {
    name = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InlineEditField value={p.itemName} onChange={p.setItemName} onSave={p.saveName} fullWidth minWidth={200} />
      </Box>
    );
  } else {
    name = (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Box
          onClick={p.startEditingName}
          sx={editableTextSx}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.itemName}</Typography>
        </Box>
        {originalTextHint}
      </Box>
    );
  }

  let model: React.ReactNode;
  if (!p.editable) {
    model = item.model_number && (
      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
        Model: {item.model_number}
      </Typography>
    );
  } else if (p.isEditingModel) {
    model = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <InlineEditField value={p.modelNumber} onChange={p.setModelNumber} onSave={p.saveModel} placeholder="Enter model number" minWidth={150} />
      </Box>
    );
  } else {
    model = (
      <Box
        onClick={p.startEditingModel}
        sx={{ ...editableTextSx, display: 'inline-block', mt: 0.25 }}
      >
        <Typography variant="caption" display="block" color="text.secondary">
          Model: {p.modelNumber || 'Click to add'}
        </Typography>
      </Box>
    );
  }

  return (
    <TableCell>
      {name}
      {model}
    </TableCell>
  );
}

const ReferencePriceIcon = ({ inline }: { inline?: boolean }) => (
  <Tooltip title="Reference price — verify before sending">
    {inline ? (
      <InfoIcon component="span" fontSize="inherit" sx={{ color: 'warning.main', fontSize: 14, cursor: 'help', verticalAlign: 'middle', ml: 0.5 }} />
    ) : (
      <InfoIcon sx={{ color: 'warning.main', fontSize: 16, cursor: 'help', mr: 0.5 }} />
    )}
  </Tooltip>
);

interface QuotedPriceCellProps {
  item: RFQItem;
  canEdit: boolean;
  price: string;
  disabled: boolean;
  onChange: ChangeHandler;
  onBlur: () => void;
  /** The validate toggle is locked while the price is being edited. */
  validateDisabled: boolean;
  notAvailableDisabled: boolean;
  onValidate: () => void;
  onMarkNotAvailable: () => void;
}

/** The quoted price with its validate and "not available" buttons. */
export function QuotedPriceCell(p: QuotedPriceCellProps) {
  const { item } = p;
  const hasReferencePrice = item.price_sources?.reference_price != null;
  if (!p.canEdit) {
    return (
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
          ${p.price || "-"}
          {hasReferencePrice && <ReferencePriceIcon inline />}
        </Typography>
      </TableCell>
    );
  }
  return (
    <TableCell>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <TextField
          size="small"
          type="number"
          value={p.price}
          onChange={p.onChange}
          onBlur={p.onBlur}
          disabled={p.disabled}
          inputProps={{
            step: 0.01,
            min: 0,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.25 }}>
                {/* Reference price indicator — low-confidence AI prices should be verified */}
                {hasReferencePrice && <ReferencePriceIcon />}
                $
              </InputAdornment>
            ),
          }}
          sx={numberFieldSx(!!item.price_validated, 116)}
        />

        {/* Validation button - instant feedback with optimistic updates */}
        <Tooltip title={item.price_validated ? "Unvalidate price" : "Validate price"}>
          <span>
            <IconButton
              size="small"
              onClick={p.onValidate}
              disabled={p.validateDisabled}
              sx={{
                transition: 'all 0.15s ease-in-out',
                '&:active': {
                  transform: 'scale(0.95)',
                }
              }}
            >
              {item.price_validated ? (
                <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />
              ) : (
                <CheckCircleOutline fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {/* Item Not Available button */}
        <Tooltip title="Mark as item not available (sets price to $0 and validates)">
          <span>
            <IconButton
              size="small"
              onClick={p.onMarkNotAvailable}
              disabled={p.notAvailableDisabled}
              color="default"
            >
              <RemoveCircleOutline fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </TableCell>
  );
}

/** A number field with a unit adornment, or its value as text when read-only. */
export function NumberCell({ canEdit, value, onChange, onBlur, disabled, step, unit, width, validated, readOnlyText }: {
  canEdit: boolean;
  value: string;
  onChange: ChangeHandler;
  onBlur: () => void;
  disabled?: boolean;
  step: number;
  unit: string;
  width: number;
  validated: boolean;
  readOnlyText: string;
}) {
  return (
    <TableCell>
      {canEdit ? (
        <TextField
          size="small"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          type="number"
          inputProps={{
            step,
            min: 0,
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
          }}
          sx={numberFieldSx(validated, width)}
        />
      ) : (
        <Typography variant="body2">{readOnlyText}</Typography>
      )}
    </TableCell>
  );
}

interface RemarksCellProps {
  canEdit: boolean;
  isNonRefundable: boolean;
  isLimitedStock: boolean;
  onToggle: (tag: 'nonRefundable' | 'limitedStock') => void;
  customRemark: string;
  onOpenRemarkPopover: (e: React.MouseEvent<HTMLButtonElement>) => void;
  remarkPopover: Omit<React.ComponentProps<typeof RemarkPopover>, 'disabled'>;
  disabled: boolean;
  readOnlyText: string;
}

/** The two tag switches, the custom remark and its editor. */
export function RemarksCell(p: RemarksCellProps) {
  if (!p.canEdit) {
    return (
      <TableCell>
        <Typography variant="body2">
          {p.readOnlyText}
        </Typography>
      </TableCell>
    );
  }
  return (
    <TableCell>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1, rowGap: 0.25, alignItems: "center" }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={p.isNonRefundable}
                onChange={() => p.onToggle('nonRefundable')}
                color="primary"
              />
            }
            label={<Typography variant="body2" sx={switchLabelSx}>Non-returnable</Typography>}
            sx={{ m: 0, gap: 0.5 }}
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={p.isLimitedStock}
                onChange={() => p.onToggle('limitedStock')}
                color="primary"
              />
            }
            label={<Typography variant="body2" sx={switchLabelSx}>Limited Stock</Typography>}
            sx={{ m: 0, gap: 0.5 }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={p.onOpenRemarkPopover}
            startIcon={<CommentIcon />}
            sx={{ flexShrink: 0, py: 0.25, bgcolor: 'background.paper' }}
          >
            {p.customRemark ? 'Edit Remark' : 'Add Remark'}
          </Button>
          {/* The remark that goes out with the quote, visible without opening the editor */}
          {p.customRemark && (
            <Tooltip title={p.customRemark} enterDelay={400} placement="top-start">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  minWidth: 0,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  overflowWrap: 'anywhere',
                }}
              >
                {p.customRemark}
              </Typography>
            </Tooltip>
          )}
        </Box>

        <RemarkPopover {...p.remarkPopover} disabled={p.disabled} />
      </Box>
    </TableCell>
  );
}
